import posthog from 'posthog-js';

let initialized = false;
let analyticsDisabledDueToError = false;

const runNonBlocking = (fn) => {
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(fn);
    return;
  }

  setTimeout(fn, 0);
};

const safeRun = (operationName, fn) => {
  try {
    return fn();
  } catch (error) {
    analyticsDisabledDueToError = true;
    console.error(`📊 PostHog ${operationName} failed. Analytics disabled for this session.`, error);
    return undefined;
  }
};

const getPostHogConfig = () => {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  const apiHost = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
  const enabledFlag = (import.meta.env.VITE_POSTHOG_ENABLED ?? 'true').toString().toLowerCase();

  return {
    apiKey,
    apiHost,
    enabled: enabledFlag !== 'false',
  };
};

export const isAnalyticsEnabled = () => {
  const { apiKey, enabled } = getPostHogConfig();
  return !!apiKey && enabled && !analyticsDisabledDueToError;
};

export const initAnalytics = () => {
  if (initialized) return;

  const { apiKey, apiHost, enabled } = getPostHogConfig();
  if (!enabled) {
    console.info('📊 PostHog disabled (VITE_POSTHOG_ENABLED=false)');
    return;
  }

  if (!apiKey) {
    console.info('📊 PostHog disabled (missing VITE_POSTHOG_KEY)');
    return;
  }

  safeRun('init', () => {
    posthog.init(apiKey, {
      api_host: apiHost,
      autocapture: false,
      capture_pageview: false,
      persistence: 'localStorage+cookie',
      person_profiles: 'identified_only',
      loaded: (instance) => {
        if (import.meta.env.DEV) {
          instance.debug();
        }
      },
    });

    initialized = true;

    posthog.capture('analytics_initialized', {
      app_env: import.meta.env.MODE,
      hostname: window.location.hostname,
      pathname: window.location.pathname,
    });
  });
};

export const captureEvent = (eventName, properties = {}) => {
  if (!initialized || !isAnalyticsEnabled()) return;
  runNonBlocking(() => {
    safeRun('capture', () => {
      posthog.capture(eventName, {
        app_env: import.meta.env.MODE,
        ...properties,
      });
    });
  });
};

export const capturePageView = ({ pathname, search, title } = {}) => {
  captureEvent('$pageview', {
    pathname,
    search,
    title,
  });
};

export const identifyUser = (userId, properties = {}) => {
  if (!initialized || !isAnalyticsEnabled() || !userId) return;
  runNonBlocking(() => {
    safeRun('identify', () => {
      posthog.identify(userId, {
        app_env: import.meta.env.MODE,
        ...properties,
      });
    });
  });
};

export const resetAnalytics = () => {
  if (!initialized || !isAnalyticsEnabled()) return;
  runNonBlocking(() => {
    safeRun('reset', () => {
      posthog.reset();
    });
  });
};
